import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import useHttpHook from "../../Hooks/HttpHook";
import { getAmountOnCancelEvent } from "../../utils/ApiConstant";
import {
  dateFormatter,
  getEventImageUrl,
  getCurrentDate,
  trimString,
  convertToTimeZone,
} from "../../utils/Helper";
import moment from "moment";
import momentTimezone from "moment-timezone";
import MainComponentWrapper from "../Common/MainComponentWrapper";
import { SocialLinkModal } from "./SocialLinkModal";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CancelRequestModal } from "../Common/Modal/CancelRequestModal";
import { showErrorToast } from "../../utils/notify";

const getEventStatus = ({ eventTiming, isActive, isCancelled }) => {
  if (
    new Date(eventTiming.endDate) <
    new Date().setDate(new Date().getDate() - 1)
  ) {
    return { label: "Expired", className: "is-expired" };
  }
  if (isCancelled && !isActive) {
    return { label: "Cancelled", className: "is-cancelled" };
  }
  if (!isActive) {
    return { label: "Draft", className: "is-draft" };
  }
  if (getCurrentDate(eventTiming.startDate).diff(getCurrentDate()) > 0) {
    return { label: "Upcoming", className: "is-upcoming" };
  }
  return { label: "Active", className: "is-active" };
};

const Event = (props) => {
  const navigate = useNavigate();
  const [loading, sendRequest, error] = useHttpHook();
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [showSocialIconModal, setShowSocialIconModal] = useState(null);
  const [timezone, setTimeZone] = useState(null);
  const [isEditTicket, setIsEditTicket] = useState(false);
  const { userDetails } = useSelector((state) => state.user);
  const [amount, setAmount] = useState(null);
  const { userName } = useParams();
  const query = new URLSearchParams(window.location.search);
  const queryStatus = query.get("status");
  const querySearch = query.get("search");
  const location = useLocation();

  const eventStatus = getEventStatus(props);
  const previewImage = props.mainimage?.length
    ? getEventImageUrl(props.mainimage)
    : "";
  const detailsUrl = `/event-details/${props.Id}?page=myevents&redirectUrl=${location.pathname}?status=${queryStatus}&search=${querySearch}`;

  const handleAmount = async () => {
    try {
      const response = await sendRequest(
        `${getAmountOnCancelEvent}/${props.Id}`,
        {
          method: "get",
        }
      );
      if (response.status === 1) {
        props.getEvents();
        setAmount(response.data);
      }
    } catch (requestError) {
      console.log(requestError);
    }
  };

  useEffect(() => {
    setTimeZone(
      convertToTimeZone(
        new Date(props.eventTiming?.startDate || new Date()),
        props.eventTiming.timeZone || "Asia/Calcutta"
      )
    );
    checkSalesStart();
  }, [props]);

  useEffect(() => {
    if (error) {
      if (toast.isActive(error) === false) {
        showErrorToast(error);
        if (error === "You are unAuthrised") {
          window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
        }
      }
    }
  }, [error]);

  const checkSalesStart = () => {
    const tickets = props?.ticketType;
    for (let i = 0; i < tickets?.length; i++) {
      if (tickets[i].quantity < tickets[i].total_qty) {
        setIsEditTicket(true);
        break;
      }
    }
  };

  useEffect(() => {
    handleAmount();
  }, [isDeleteModal]);

  const canManageBeforeStart =
    getCurrentDate(props.eventTiming.endDate).diff(getCurrentDate()) > 0 &&
    getCurrentDate() < getCurrentDate(props.eventTiming.startDate);

  const canManageMultiDay =
    props.eventTiming.eventDayType === "multi" &&
    !props.isCancelled &&
    props.isActive &&
    (() => {
      try {
        const endLabel = moment(props.eventTiming.endDate).format(
          "YYYY-MM-DD,h:mm a"
        );
        const zone = props.eventTiming?.timeZone;
        const nowMoment = momentTimezone();
        const nowLabel =
          zone && momentTimezone.tz.zone(zone)
            ? nowMoment.tz(zone).format("YYYY-MM-DD,h:mm a")
            : nowMoment.format("YYYY-MM-DD,h:mm a");
        return endLabel > nowLabel;
      } catch (error) {
        return false;
      }
    })();

  const showShareButton =
    !props.isCancelled &&
    props.isActive &&
    getCurrentDate(props.eventTiming.endDate) > getCurrentDate();

  const showOwnerActions = !userName || userName === userDetails.userName;
  const showPrimaryActions =
    showOwnerActions && (canManageBeforeStart || canManageMultiDay);

  const navigateToEdit = () => {
    navigate(
      isEditTicket
        ? `/myEvents/${props.Id}/details?isEdit=true`
        : `/myEvents/${props.Id}/basic_info`,
      isEditTicket
        ? {
            state: {
              isOnlyTickets: true,
              showTabs: ["Tickets", "Detail"],
            },
          }
        : undefined
    );
  };

  return (
    <MainComponentWrapper Loading={loading} errorInfo={error}>
      <article
        className="event-created-card"
        role="listitem"
        aria-label={props.title}
      >
        <Link className="event-created-card-link" to={detailsUrl}>
          <div className="event-created-card-media">
            {previewImage ? (
              <img
                className="event-created-card-image"
                src={previewImage}
                alt={props.title}
                loading="lazy"
              />
            ) : (
              <div className="event-created-card-image event-created-card-placeholder">
                <i className="bi bi-image" aria-hidden="true"></i>
              </div>
            )}
            <span
              className={`event-created-status ${eventStatus.className}`}
            >
              {eventStatus.label}
            </span>
            {props.category && (
              <span className="event-created-tag">{props.category}</span>
            )}
          </div>
          <div className="event-created-card-body">
            <h2 className="event-created-card-title">{props.title}</h2>
            <p className="event-created-card-desc">
              {trimString(props.description, 140)}
            </p>
            <div className="event-created-card-dates">
              <div>
                <strong>Start Date:</strong>{" "}
                {dateFormatter(props.eventTiming.startDate)} ({timezone})
              </div>
              <div>
                <strong>End Date:</strong>{" "}
                {dateFormatter(props.eventTiming.endDate)}
              </div>
            </div>
          </div>
        </Link>

        {(showPrimaryActions || (showOwnerActions && showShareButton)) && (
          <div className="event-created-card-footer">
            {showPrimaryActions && !props.isCancelled && (
              <>
                <button
                  type="button"
                  disabled={loading}
                  className="event-created-btn is-primary"
                  onClick={navigateToEdit}
                >
                  Edit
                </button>
                {canManageBeforeStart && (
                  <button
                    type="button"
                    disabled={loading}
                    className="event-created-btn is-cancel"
                    onClick={() => setIsDeleteModal(props.Id)}
                  >
                    Cancel
                  </button>
                )}
                {props.isActive &&
                  (canManageBeforeStart || canManageMultiDay) && (
                    <button
                      type="button"
                      disabled={loading}
                      className="event-created-btn is-primary"
                      onClick={() =>
                        navigate(`/organizer/assign/promoter/${props.Id}/`, {
                          replace: true,
                        })
                      }
                    >
                      Assign Promoter
                    </button>
                  )}
              </>
            )}
            {showOwnerActions && showShareButton && (
              <button
                type="button"
                className="event-created-share"
                aria-label={`Share ${props.title}`}
                onClick={() => setShowSocialIconModal({})}
              >
                <i className="bi bi-share" aria-hidden="true"></i>
              </button>
            )}
          </div>
        )}
      </article>

      <SocialLinkModal
        eventId={props.Id}
        show={!!showSocialIconModal}
        onHide={() => setShowSocialIconModal(null)}
      />

      <CancelRequestModal
        amount={amount}
        eventId={isDeleteModal}
        show={!!isDeleteModal}
        getEvents={props.getEvents}
        onClose={() => {
          setIsDeleteModal(null);
        }}
      />
    </MainComponentWrapper>
  );
};

export default Event;
